import { useState } from 'react';
import { useAppContext } from '../../context/AppContext';
import { connectWise } from '../../services';

const SITE_PRESETS = [
  { label: 'North America', value: 'api-na.myconnectwise.net' },
  { label: 'Europe', value: 'api-eu.myconnectwise.net' },
  { label: 'Australia', value: 'api-au.myconnectwise.net' },
  { label: 'Custom', value: 'custom' },
];

function initialState() {
  return {
    sitePreset: SITE_PRESETS[0].value,
    customSite: '',
    wiseCompanyId: '',
    publicKey: '',
    privateKey: '',
    saving: false,
    error: '',
  };
}

function resolveSite(sitePreset, customSite) {
  const raw = sitePreset === 'custom' ? customSite.trim() : sitePreset;
  return raw.replace(/^https?:\/\//i, '').replace(/\/+$/, '');
}

export const ConnectWiseModal = ({ isOpen, onClose, onSuccess }) => {
  const { base_url, token, showStatus } = useAppContext();
  const [state, setState] = useState(initialState);

  if (!isOpen) return null;

  const setField = (key, value) => {
    setState((prev) => ({ ...prev, [key]: value, error: '' }));
  };

  const site = resolveSite(state.sitePreset, state.customSite);
  const canSubmit =
    Boolean(site) &&
    Boolean(state.wiseCompanyId.trim()) &&
    Boolean(state.publicKey.trim()) &&
    Boolean(state.privateKey.trim()) &&
    !state.saving;

  const resetAndClose = () => {
    setState(initialState());
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setField('saving', true);
    try {
      const data = await connectWise(base_url, token, {
        site,
        wise_company_id: state.wiseCompanyId.trim(),
        public_key: state.publicKey.trim(),
        private_key: state.privateKey.trim(),
      });
      setState(initialState());
      showStatus('success', data.msg || 'ConnectWise connected');
      onSuccess?.(data);
    } catch (error) {
      setState((prev) => ({
        ...prev,
        saving: false,
        error: error.message || 'Failed to connect ConnectWise',
      }));
    }
  };

  return (
    <>
      <div className="modal-overlay" onClick={resetAndClose} aria-hidden="true" />
      <div className="modal" role="dialog" aria-modal="true" aria-labelledby="connectwise-modal-title">
        <div className="modal-content batch-modal-content">
          <h2 id="connectwise-modal-title" className="modal-title">
            Connect ConnectWise
          </h2>
          <p className="modal-body" style={{ marginBottom: 12 }}>
            Enter your ConnectWise PSA API credentials. Create keys under System → Members → API Keys.
            The private key is only shown once when created.
          </p>
          <div className="divider" />

          <form onSubmit={handleSubmit} className="batch-modal-form">
            <div className="form-group">
              <label className="form-label" htmlFor="cw-site-preset">
                Site / Region
              </label>
              <select
                id="cw-site-preset"
                className="form-select"
                value={state.sitePreset}
                onChange={(e) => setField('sitePreset', e.target.value)}
              >
                {SITE_PRESETS.map((preset) => (
                  <option key={preset.value} value={preset.value}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>

            {state.sitePreset === 'custom' && (
              <div className="form-group">
                <label className="form-label" htmlFor="cw-custom-site">
                  Custom hostname
                </label>
                <input
                  id="cw-custom-site"
                  className="form-input"
                  type="text"
                  placeholder="api-na.myconnectwise.net"
                  value={state.customSite}
                  onChange={(e) => setField('customSite', e.target.value)}
                  autoComplete="off"
                />
                <p className="form-hint">Hostname only — https:// is optional</p>
              </div>
            )}

            <div className="form-group">
              <label className="form-label" htmlFor="cw-company-id">
                Company ID
              </label>
              <input
                id="cw-company-id"
                className="form-input"
                type="text"
                placeholder="Same ID used on the ConnectWise login screen"
                value={state.wiseCompanyId}
                onChange={(e) => setField('wiseCompanyId', e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cw-public-key">
                Public API Key
              </label>
              <input
                id="cw-public-key"
                className="form-input"
                type="text"
                value={state.publicKey}
                onChange={(e) => setField('publicKey', e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label className="form-label" htmlFor="cw-private-key">
                Private API Key
              </label>
              <input
                id="cw-private-key"
                className="form-input"
                type="password"
                value={state.privateKey}
                onChange={(e) => setField('privateKey', e.target.value)}
                autoComplete="new-password"
              />
              <p className="form-hint">Shown only once when the key is created in ConnectWise</p>
            </div>

            {state.error && (
              <p className="form-error" role="alert" style={{ marginBottom: 8 }}>
                {state.error}
              </p>
            )}

            <button
              type="submit"
              className="btn btn-primary batch-modal-submit"
              disabled={!canSubmit}
            >
              {state.saving ? 'Connecting…' : 'Connect'}
            </button>
            <button
              type="button"
              className="btn btn-secondary batch-modal-close"
              onClick={resetAndClose}
              disabled={state.saving}
            >
              Cancel
            </button>
          </form>
        </div>
      </div>
    </>
  );
};
